<template>
  <div
    v-accessibility-outline
    :data-correlation-id="getTraceId()"
    :data-trace-id="getTraceId()"
    class="root-container">
    <component :is="ConfirmModal" />
    <session-modal v-if="isLoggedIn" data-test="session-modal" @error="routeToError" />
    <cancel-challenge-modal data-test="cancel-challenge-modal" />
    <router-view name="PageHeader" />
    <router-view v-show="!isLoading" name="StepTracker" />
    <processing v-if="isLoading" />
    <keep-alive :max="1">
      <router-view v-show="!isLoading" :aria-hidden="isLoading" />
    </keep-alive>
    <router-view v-show="!isLoading" :aria-hidden="isLoading" name="interrupt" />
    <router-view name="PageFooter" />
    <tux-locale-picker
      v-model="appLocale"
      name="locale-action-sheet"
      @open="handleLocalePickerOpen"
      @confirm="handleLocalePickerConfirm"
      @dismiss="handleLocalePickerDismiss" />
  </div>
</template>

<script>
import { lumber } from '@mc-alberta/lumber'
import { getWebAuthNSupport } from '@mc-alberta/supernova-scripts'
import { TuxLocalePicker } from '@mc-alberta/tuxedo'
import ttiPolyfill from 'tti-polyfill'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import CancelChallengeModal from '@/components/CancelChallengeModal.vue'
import SessionModal from '@/components/SessionModal'
import useConfirmModal from '@/composables/useConfirmModal'
import constants, { ACTION, ROUTES } from '@/constants'
import Processing from '@/pages/Processing'
import * as types from '@/store/mutation-types'
import { getTraceId } from '@/utils/traceId'

export default {
  name: 'App',
  components: {
    Processing,
    SessionModal,
    TuxLocalePicker,
    CancelChallengeModal
  },
  setup(props, context) {
    const { ConfirmModal } = useConfirmModal({ context })
    return { ConfirmModal }
  },
  data() {
    return {
      windowHeight: 0,
      timer: null
    }
  },
  computed: {
    ...mapGetters(['serviceErrorResponse', 'isLoading', 'isLoggedIn', 'locale']),
    ...mapState({
      action: (state) => state.action
    }),
    appLocale: {
      get() {
        return this.locale
      },
      set(newLocale) {
        this.handleLocaleUpdate(newLocale)
      }
    }
  },
  async created() {
    window.addEventListener('resize', () => {
      clearTimeout(this.timer)

      this.timer = setTimeout(() => {
        this.watchForHeightChanges()
      }, 500)
    })

    window.requestAnimationFrame(this.watchForHeightChanges)

    const queryParams = new URLSearchParams(window.location.search)
    const webAuthRes = await getWebAuthNSupport()

    await this.init({ params: queryParams, webAuthRes })
    if (queryParams.get('initiator') === constants.DCF) {
      await this.handleUserFromDCF()
    } else if (this.action === ACTION.EMAIL_CONFIRMATION) {
      this.handleEmailConfirmation()
    }

    // if no route is specified, default to register page
    if (!this.$route.name) {
      this.$router.replace({ name: ROUTES.ENTRY })
    }

    this.stopLoader(constants.LOADERS.APP_INIT)
  },
  async mounted() {
    /* istanbul ignore next */
    window.onunhandledrejection = ({ reason = {} }) => {
      try {
        // axios is already logging xhr request/response errors,
        // so we only care about non http related errors here. ~ Ronald
        if (reason.name !== 'GenericSamwellError' && !reason.request) {
          lumber.error({
            event: 'BROWSER_EXCEPTION',
            details: { message: reason.stack }
          })
        }
      } catch (error) {
        // in case the try clause fails we do not want this handler to loop ~ Kerry
        console.error(error)
      }
    }

    const tti = await ttiPolyfill.getFirstConsistentlyInteractive()
    lumber.info([
      { event: 'PERF_FP', details: { duration: window['first-paint'], timestamp: Date.now() } },
      {
        event: 'PERF_FCP',
        details: { duration: window['first-contentful-paint'], timestamp: Date.now() }
      },
      { event: 'PERF_TTI', details: { duration: tti, timestamp: Date.now() } }
    ])
  },

  methods: {
    getTraceId,
    ...mapActions([
      'init',
      'clearServiceLayerError',
      'setMessagePageContent',
      'fetchVerificationId',
      'loadCustomerProfile',
      'stopLoader',
      'setLocale',
      'setPreviousLocale'
    ]),
    ...mapMutations({
      setActionIsPending: types.SET_ACTION_IS_PENDING,
      setAnalyticsFeature: types.SET_ANALYTICS_FEATURE,
      setLocaleSelectorSelected: types.SET_LOCALE_SELECTOR_SELECTED,
      setLocaleSelectorActive: types.SET_LOCALE_SELECTOR_ACTIVE
    }),
    handleLocaleUpdate(confirmedLocale) {
      if (!confirmedLocale || this.locale === confirmedLocale) return

      this.setLocale(confirmedLocale)
    },
    handleLocalePickerOpen({ currentLocale }) {
      if (!currentLocale) return

      this.setPreviousLocale(currentLocale)
      this.setLocaleSelectorSelected(true)
      this.setLocaleSelectorActive(true)
    },
    handleLocalePickerConfirm(confirmedLocale) {
      if (!confirmedLocale) return

      this.trackLocaleChanges(this.$EVENTS.CHANGE_LOCALE_CONFIRM)
      this.setLocaleSelectorActive(false)
      this.setLocale(confirmedLocale)
    },
    handleLocalePickerDismiss() {
      this.trackLocaleChanges(this.$EVENTS.CHANGE_LOCALE_DISMISS)
      this.setLocaleSelectorActive(false)
    },
    routeToError() {
      this.setMessagePageContent({
        buttonText: 'button.ok',
        buttonTarget: ROUTES.ENTRY
      })
      this.$router.replace({ name: ROUTES.ERROR })
    },
    async loadProfile() {
      await this.loadCustomerProfile()

      const errorResponse = this.serviceErrorResponse()
      if (errorResponse) {
        this.routeToError()
      } else {
        this.$router.replace({
          name: ROUTES.PROFILE_HOME,
          params: { isNewUser: false, skipConfirmation: true }
        })
      }
    },
    async handleUserFromDCF() {
      const error = this.serviceErrorResponse()

      if (error && error.status === 401) {
        this.clearServiceLayerError()
        await this.fetchVerificationId()

        if (!this.serviceErrorResponse()) {
          return this.$router.push({
            name: ROUTES.VERIFY_ACCOUNT,
            params: { onSuccess: this.loadProfile }
          })
        }
      }

      this.$router.replace({ name: ROUTES.ENTRY })
    },
    handleEmailConfirmation() {
      const error = this.serviceErrorResponse()

      if (error) {
        this.$router.replace({ name: ROUTES.ENTRY })
      } else {
        this.$router.replace({ name: ROUTES.EMAIL_CONFIRMATION })
      }
    },
    watchForHeightChanges() {
      /**
       * Account for chrome adding height for mobile toolbars
       * {@link https://bugs.webkit.org/show_bug.cgi?id=141832}
       */
      if (window.innerHeight !== this.windowHeight) {
        this.windowHeight = window.innerHeight

        document.documentElement.style.setProperty(
          '--src-webkit-window-height',
          `${window.innerHeight}px`
        )
        window.requestAnimationFrame(this.watchForHeightChanges)
      }
    }
  }
}
</script>

<style>
@media screen and (max-device-width: 768px) and (-webkit-min-device-pixel-ratio: 0) {
  body {
    height: auto;
    height: var(--src-webkit-window-height);
  }
}
</style>
